import template from './mx-shell-navigation-panel.html';
import { DisplayMode } from '../../../Shell/Pandora/model/display-modes';
import userlaneIcon from '../../../Shell/assets/images/modern_M42-avatar.jpg';

/**
 * @ngdoc component
 * @name mx.shell:mxShellNav
 * @module mx.shell
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$document" target="_blank">$document</a>
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$timeout" target="_blank">$timeout</a>
 * @requires <a href="https://material.angularjs.org/latest/api/service/$mdSidenav" target="_blank">$mdSidenav</a>
 * @requires {@ref mx.internationalization mx.internationalization}
 * @requires {@ref mx.shell.NavigationService mx.shell.NavigationService}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 * @requires {@ref mx.shell.NavigationManager mx.shell.NavigationManager}
 * @requires {@ref mx.shell.Config mx.shell.Config}
 * @description
 * Side panel that contains navigation items
 * @usage <mx-shell-nav layout="column"></mx-shell-nav>
 */

export const mxShellNavigationPanelComponent = {
	controller: MxShellNavigationPanelCtrl,
	controllerAs: 'vm',
	template,
	bindings: {
		horizontalNavMode: '<',
	},
};

MxShellNavigationPanelCtrl.$inject = [
	'mx.internationalization',
	'mx.shell.NavigationService',
	'mx.shell.EventService',
	'mx.shell.NavigationManager',
	'$scope',
	'$element',
	'$mdConstant',
	'$document',
	'mx.app.DeviceService',
	'$mdSidenav',
	'$window',
	'mx.shell.KeyboardNavigationService',
	'$timeout',
	'mx.shell.RoutingService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.shell.AppsService',
	'mx.shell.UserlaneService',
];

function MxShellNavigationPanelCtrl(
	i18n,
	navigationService,
	eventService,
	navigationManager,
	$scope,
	$element,
	$mdConstant,
	$document,
	deviceService,
	$mdSidenav,
	$window,
	keyboardNavigationService,
	$timeout,
	routingService,
	personalizationService,
	appsService,
	userlaneService
) {
	const vm = this;

	const collapsedWidth = 64;

	const defaultWidth = 260;
	let navWidth = defaultWidth;
	vm.showNavigation = true;

	vm.navigationManager = navigationManager;
	vm.navigationService = navigationService;
	vm.deviceService = deviceService;
	vm.keyboardNavigationService = keyboardNavigationService;
	vm.userlaneService = userlaneService;
	vm.userlaneIcon = userlaneIcon;

	vm.changeFilterText = changeFilterText;

	const _settingsLabel = i18n.get('shell.common.settings').toLowerCase();
	vm.isSettingLabelVisible = (searchText) => !searchText || _settingsLabel.includes(searchText.toLowerCase());

	vm.$onInit = function () {
		vm.isVisible = true;
		personalizationService.getNavigationWidth().then((personalizedWidth) => {
			if (!isNaN(personalizedWidth)) {
				navWidth = personalizedWidth;
			}
		});
		navigationService.get().then((navItems) => {
			vm.isNavLoaded = true;
			init(navItems);
			initKeyboardControl();
		});

		vm.isVisible = vm.navigationManager.isVisible;
		if (!vm.horizontalNavMode) {
			$element[0].style.maxWidth = vm.isVisible ? `${collapsedWidth}px` : '0';
		}
		initWatchers();
		registerHotkeys();
		initAria();
		handle2ndLevelDropdowns();
		initTwoPanelContextNavCollapse();
	};

	vm.$onDestroy = function () {
		$window.removeEventListener('click', onClickOutside, true);
	};

	eventService.subscribeTo(
		mx.shell.EventCodes.NavigationLoaded,
		(event, { navItems }) => {
			init(navItems);
		},
		$scope
	);

	eventService.subscribeTo(
		mx.shell.EventCodes.DisplayModeChanged,
		function (event, newMode) {
			vm.isVisible = newMode === DisplayMode.Normal && vm.navigationManager.isVisible;
			let styleValue = vm.isVisible ? '' : '0 !important';

			styleValue = 'left: ' + styleValue + '; top: ' + styleValue + ';';
			$('.mx-main-content-zone-container').attr('style', styleValue);
		},
		$scope
	);

	eventService.subscribeTo(
		mx.shell.EventCodes.NavigationItemClicked,
		() => {
			if (!vm.navigationManager.hoverMode && !vm.navigationManager.pinnedMode && !vm.navigationManager.collapsedMode) {
				changeFilterText('');

				$timeout(() => {
					setCollapseMode(true, true);
				}, 20);
			}
		},
		$scope
	);

	eventService.subscribeTo(
		mx.shell.EventCodes.NavigationCollapsed,
		(event, { collapsed }) => {
			if (collapsed) {
				changeFilterText('');
			}
		},
		$scope
	);

	eventService.subscribeTo(mx.shell.EventCodes.LayoutChanged, () => updateToolbarHorizontalNavCompactMode(), $scope);

	vm.onSearchClick = function () {
		vm.navigationManager.setCollapseMode(false, true);
	};

	vm.handleBurgerBtn = function () {
		if (deviceService.screenMobile()) {
			$mdSidenav('mx-shell-left-sidebar').toggle();
			eventService.dispatchEvent(mx.shell.EventCodes.NavToggled);
		} else {
			navigationManager.toggleCollapseMode(true);
		}
	};

	vm.onPinNavigationClick = function () {
		navigationManager.togglePinMode();
	};

	vm.onHoverNavigationCLick = function () {
		navigationManager.toggleHoverMode();
	};

	vm.goToDefaultRoot = function () {
		routingService.goToDefault();
	};

	vm.close = closeNav;

	vm.navigationResized = function (width) {
		personalizationService.setNavigationWidth(width);
		navWidth = width;
		setNavSizes(navWidth);
		_debouncedLayoutChangeDispatcher();
	};

	function initTwoPanelContextNavCollapse() {
		let savedCollapsedMode = null;

		eventService.subscribeTo(
			mx.shell.EventCodes.SideLayoutWideModeSwitched,
			(event, isFullScreen) => {
				if (isFullScreen && !deviceService.screenExtraWide()) {
					if (savedCollapsedMode === null) {
						savedCollapsedMode = navigationManager.collapsedMode;
					}
					navigationManager.setCollapseMode(true, true, true);
				} else if (savedCollapsedMode !== null) {
					navigationManager.setCollapseMode(savedCollapsedMode, true, true);
					savedCollapsedMode = null;
				}
			},
			$scope
		);

		eventService.subscribeTo(
			mx.shell.EventCodes.SidePanelVisibility,
			(event, visible) => {
				if (savedCollapsedMode === null) {
					return;
				}

				if (!visible) {
					navigationManager.setCollapseMode(savedCollapsedMode, true, true);
					savedCollapsedMode = null;
				}
			},
			$scope
		);
	}

	const _debouncedLayoutChangeDispatcher = $.debounce(500, function () {
		eventService.dispatchEvent(mx.shell.EventCodes.LayoutChanged);
	});

	const updateToolbarHorizontalNavCompactMode = $.debounce(300, function () {
		if (!vm.horizontalNavMode) {
			return;
		}
		const navListEl = $element.find('.mx-shell-nav-list');
		navListEl.removeClass('mx-shell-nav--toolbarMode-compact');
		if ($element.width() < navListEl.width() + 32) {
			navListEl.addClass('mx-shell-nav--toolbarMode-compact');
		}
	});

	$window.addEventListener('click', onClickOutside, true);

	return vm;

	function handle2ndLevelDropdowns() {
		if (vm.horizontalNavMode) {
			return;
		}

		// Handle case when scroll appears on 1st level and it's hard to use it
		let prevNavScrollbarWidth = 0;
		$element.on('mouseenter', '.mx-shell-nav--list-container', function () {
			if (vm.navigationManager.collapsedMode) {
				const navContainer = $(this);
				const navScrollbarWidth = this.offsetWidth - this.clientWidth;
				if (navScrollbarWidth !== prevNavScrollbarWidth) {
					navContainer.find('.mx-shell-nav-item--hover-2nd-level-supporter').css('width', `calc(100% - ${navScrollbarWidth}px)`);
					navContainer.find('.mx-shell-nav-item--list-wrap').css('margin-inline-start', navScrollbarWidth ? `-${navScrollbarWidth}px` : '');
					prevNavScrollbarWidth = navScrollbarWidth;
				}
			}
		});

		// Handle case when 2nd level dropdowns has wrong position
		$element.on('mouseenter', '.mx-shell-nav-item.mx-shell-nav-item--level-0', function () {
			if (vm.navigationManager.collapsedMode) {
				// Handle case when 2nd level dropdowns has wrong top possition
				const topShift = 34;
				const buttonTop = $(this).position().top;
				const dropdown = $(this).find('.mx-shell-nav-item--list-wrap:visible');
				if (dropdown.length) {
					dropdown.css({ top: 'auto', bottom: 'auto' });

					const dropdownTop = dropdown.position().top;
					if (dropdownTop > buttonTop + topShift) {
						dropdown.css({ top: `${buttonTop + topShift}px` });
					}

					// Handle case when 2nd level dropdowns go down beyond the window viewport
					const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
					const dropdownBottom = dropdown[0].getBoundingClientRect().bottom;
					if (height < dropdownBottom) {
						dropdown.css({ top: 'auto', bottom: '4px' });
					}
				}
			}
		});
	}

	function setCollapseMode(value, force = false) {
		if (!vm.navigationManager.collapsedMode && value) {
			$element[0].style.pointerEvents = 'none';
			$timeout(() => ($element[0].style.pointerEvents = 'auto'), 550); //TODO: to rework on transitionstart/transitionend, and for all elements in panel
		}
		vm.navigationManager.setCollapseMode(value, force);
	}

	function onClickOutside($event) {
		if ($element[0] === $event.target || $element[0].contains($event.target) || vm.navigationManager.pinnedMode) {
			return;
		}

		if (!vm.navigationManager.collapsedMode) {
			setCollapseMode(true);
		}
	}

	function initWatchers() {
		$scope.$watch('vm.navigationManager.collapsedMode', function () {
			if (vm.horizontalNavMode) {
				return;
			}
			if (vm.navigationManager.collapsedMode !== undefined) {
				setNavSizes(vm.navigationManager.collapsedMode ? collapsedWidth : navWidth);
			}
		});

		$scope.$watchGroup(['vm.navigationManager.pinnedMode', 'vm.navigationManager.isVisible'], function () {
			if (vm.horizontalNavMode) {
				updateToolbarHorizontalNavCompactMode();

				return;
			}
			$element[0].style.maxWidth = !vm.navigationManager.isVisible || vm.navigationManager.pinnedMode ? '' : `${collapsedWidth}px`;
		});
	}

	function closeNav() {
		$mdSidenav('mx-shell-left-sidebar').close();
	}

	function setNavSizes(navWidth) {
		document.documentElement.style.setProperty('--mx-shell-nav-width', navWidth + 'px');
		_debouncedLayoutChangeDispatcher();
	}

	function registerHotkeys() {
		keyboardNavigationService.registerKey({
			callback: $.throttle(300, function () {
				$scope.$evalAsync(function () {
					$element.find('.mx-burger-btn').trigger('focus').trigger('click');
				});
			}),
			description: i18n.get('shell.hotkeys.toggleNavigation'),
			key: ['alt+/', 'alt+shift+7'],
			nameInTemplate: 'sidebarHint',
			scope: $scope,
		});
	}

	function initAria() {
		setTimeout(() => $element.find('md-tabs-canvas, md-tab-item').attr('tabindex', '-1'), 500);

		vm.appTitle = appsService.getCurrentApplication().title;
	}

	function init(navItems) {
		vm.navItems = navItems;
	}

	function changeFilterText(filterText) {
		const oldFilterText = vm.navigationManager.filterText;

		if ((filterText || (oldFilterText && !filterText)) && filterText !== oldFilterText) {
			navigationService.markFilterTextMatched(vm.navItems, filterText.toLowerCase(), undefined, (filterText || '').length > (oldFilterText || '').length);
			vm.navigationManager.filterText = filterText;
		}
	}

	function initKeyboardControl() {
		let allNavItems;

		$element.keydown(({ keyCode }) => {
			const activeEl = $($document[0].activeElement);
			if (activeEl.is('.mx-shell-nav-item--btn:visible .mx-shell-nav-item__item-text')) {
				if (keyCode === $mdConstant.KEY_CODE.ESCAPE) {
					activeEl.blur();
				}

				if (keyCode === $mdConstant.KEY_CODE.DOWN_ARROW) {
					_getClosestSibling(activeEl, true).focus();
				}

				if (keyCode === $mdConstant.KEY_CODE.UP_ARROW) {
					_getClosestSibling(activeEl, false).focus();
				}
			}
		});

		eventService.subscribeTo(
			mx.shell.EventCodes.NavigationItemClicked,
			() => {
				allNavItems = undefined;
			},
			$scope
		);

		function _getClosestSibling(activeEl, downOtherwiseUp) {
			let res = activeEl;
			allNavItems = $element.find('.mx-shell-nav-item--btn:visible .mx-shell-nav-item__item-text');
			const currentNavItemIndex = allNavItems.index(activeEl);
			if (downOtherwiseUp && currentNavItemIndex !== -1 && currentNavItemIndex + 1 < allNavItems.length) {
				res = $(allNavItems[currentNavItemIndex + 1]);
			} else if (!downOtherwiseUp && currentNavItemIndex > 0) {
				res = $(allNavItems[currentNavItemIndex - 1]);
			}
			return res;
		}
	}
}
